import { FunctionComponent, MutableRefObject, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import PreviewWrapper from './preview.style';
import { getStaticImageUrl } from '@utils/createImageUrl';
import LawtalkLogo from 'public/icon/LawtalkLogo';
import { cx } from '@emotion/css';
import Pie from '@component/Pie';
import HeadMetaTag from '@component/HeadMetaTags';
import TopBanner from '@component/Preview/Top';
import OneDay from '@component/Preview/OneDay';
import FourteenDay from '@component/Preview/FourteenDay';
import AdviceProcess from '@component/Preview/AdviceProcess';
import ThirtyDay from '@component/Preview/ThirtyDay';
import AdDesc from '@component/Preview/AdDesc';
import LawyerProfileImage from '@component/Preview/LawyerProfileImage';
import AdviceSchedule from '@component/Preview/AdviceSchedule';
import AdviceAfter from '@component/Preview/AdviceAfter';
import ThirtyFiveDay from '@component/Preview/ThirtyFiveDay';
import Guide from '@component/Preview/Guide';

interface sectionType {
  type: 'sticky' | 'normal';
  scrollExpandNum: number;
  scrollHeight: number;
  widthResizeNum?: number;
  heightResizeNum?: number;
  elements: {
    container: MutableRefObject<any>;
    mobileImage?: MutableRefObject<any>;
    content?: MutableRefObject<any>;
  };
}

const Preview: FunctionComponent = ({ isMobile }: { isMobile?: boolean }) => {
  const topBannerElem = useRef<any>(null);
  const oneDayElem = useRef<any>(null);
  const emptyLawyerImageElem = useRef<any>(null);
  const lawyerImageElem = useRef<any>(null);
  const oneDayMobileImg = useRef<any>(null);
  const oneDayContentElem = useRef<any>(null);
  const thirtyDayElem = useRef<any>(null);
  const fourteenDayElem = useRef<any>(null);
  const adviceElem = useRef<any>(null);
  const adviceAfterElem = useRef<any>(null);
  const adDescElem = useRef<any>(null);
  const thirtyFiveDayElem = useRef<any>(null);
  const adviceScheduleElem = useRef<any>(null);

  let mobileImageWidthSizeInOneDay = isMobile ? 215 : 940;
  let mobileImageHeightSizeInOneDay = isMobile ? 441 : 609;

  const sections: sectionType[] = [
    {
      type: 'sticky',
      scrollExpandNum: 1,
      scrollHeight: 0,
      widthResizeNum: 0,
      heightResizeNum: 0,
      elements: {
        container: topBannerElem,
      },
    },
    {
      type: 'sticky',
      scrollExpandNum: 3,
      scrollHeight: 0,
      elements: {
        container: oneDayElem,
        mobileImage: oneDayMobileImg,
        content: oneDayContentElem,
      },
    },
    {
      type: 'normal',
      scrollExpandNum: 1,
      scrollHeight: 0,
      elements: {
        container: emptyLawyerImageElem,
      },
    },
    {
      type: 'normal',
      scrollExpandNum: 1,
      scrollHeight: 0,
      elements: {
        container: lawyerImageElem,
      },
    },
    {
      type: 'normal',
      scrollExpandNum: 1,
      scrollHeight: 0,
      elements: {
        container: fourteenDayElem,
      },
    },
    {
      type: 'sticky',
      scrollExpandNum: 3,
      scrollHeight: 0,
      elements: {
        container: adviceElem,
      },
    },
    {
      type: isMobile ? 'sticky' : 'normal',
      scrollExpandNum: isMobile ? 3 : 1,
      scrollHeight: 0,
      elements: {
        container: thirtyDayElem,
      },
    },
    {
      type: 'normal',
      scrollExpandNum: 1,
      scrollHeight: 0,
      elements: {
        container: adDescElem,
      },
    },
    {
      type: 'normal',
      scrollExpandNum: 1,
      scrollHeight: 0,
      elements: {
        container: thirtyFiveDayElem,
      },
    },
    {
      type: 'normal',
      scrollExpandNum: 1,
      scrollHeight: 0,
      elements: {
        container: adviceScheduleElem,
      },
    },
    {
      type: isMobile ? 'sticky' : 'normal',
      scrollExpandNum: isMobile ? 3 : 1,
      scrollHeight: 0,
      elements: {
        container: adviceAfterElem,
      },
    },
  ];

  const setLayout = () => {
    for (let i = 0; i < sections.length; i++) {
      if (sections[i].type === 'normal') {
        sections[i].scrollHeight = sections[i].elements?.container?.current.clientHeight;
      }

      const container = sections[i].elements?.container;
      if (sections[i].type === 'sticky' && container !== undefined && container.current !== null) {
        sections[i].scrollHeight = sections[i].scrollExpandNum * window.innerHeight;
        container.current.style.height = `${sections[i].scrollHeight}px`;
      }
    }
  };

  const setActiveCircle = (sliceCircles: Element[], activeIndex: number) => {
    for (let i = 0; i < sliceCircles.length; i++) {
      const element = sliceCircles[i];
      if (i === activeIndex) {
        element.classList.add('active');
      } else if (element.classList.contains('active')) {
        element.classList.remove('active');
      }
    }
  };

  const resetTopBanner = (firstSceneElem: sectionType['elements']) => {
    const bannerContent: any = firstSceneElem.container?.current.querySelector('.for-lawyer__top-banner-content');
    if (bannerContent) {
      const bannerClientRect = bannerContent.getBoundingClientRect();
      const bannerRelativeTop = bannerClientRect.top;

      const mobileClientRect = sections[1].elements?.mobileImage?.current.getBoundingClientRect();
      const mobileRelativeTop = mobileClientRect.top;

      if (bannerRelativeTop >= mobileRelativeTop) {
        bannerContent.classList.add('invisible-background');
        const firstMobileImage = bannerContent.querySelector('.for-lawyer-mobile-profile-image');
        firstMobileImage?.classList.add('active');
        const vlogTitle = bannerContent.querySelector('.for-lawyer-top-banner-vlog__title');
        vlogTitle.classList.add('invisible');
        sections[1].elements?.mobileImage?.current.appendChild(bannerContent);
      }
    }
  };

  const removeStickyOfOneDaySection = () => {
    if (oneDayContentElem.current.classList.contains('active')) {
      oneDayContentElem.current.classList.remove('active');
    }

    const mobileImageElem = document.querySelector('.for-lawyer__top-banner-content');
    if (!mobileImageElem) {
      return;
    }
    const firstMobileImage = mobileImageElem.querySelector('.for-lawyer-mobile-profile-image');
    const secondMobileImage = mobileImageElem.querySelector('.for-lawyer-mobile-profile-price-image');
    const thirdMobileImage = mobileImageElem.querySelector('.for-lawyer-mobile-profile-price-law-image');
    firstMobileImage?.classList.remove('active');
    secondMobileImage?.classList.remove('active');
    thirdMobileImage?.classList.remove('active');
  };

  const removeStickyOfAdviceProcess = () => {
    const adviceContent = adviceElem.current.querySelector('.for-lawyer__advice-content');
    if (adviceContent.classList.contains('fixed')) {
      adviceContent.classList.remove('fixed');
    }

    const firstStepDesc = adviceElem.current.querySelector('.for-lawyer-advice-step-1');
    const secondStepDesc = adviceElem.current.querySelector('.for-lawyer-advice-step-2');
    const thirdStepDesc = adviceElem.current.querySelector('.for-lawyer-advice-step-3');

    firstStepDesc.classList.remove('active');
    secondStepDesc.classList.remove('active');
    thirdStepDesc.classList.remove('active');
  };

  const controlScheduleImage = (scheduleContainerElem: sectionType['elements']) => {
    const adviceScheduleImgContainer =
      scheduleContainerElem.container?.current.querySelector('.for-lawyer-advice-mobile');
    const adviceScheduleImgRect = adviceScheduleImgContainer.getBoundingClientRect();

    const adviceScheduleImg = scheduleContainerElem.container?.current.querySelector(
      '.for-lawyer-manager-schedule-img',
    );

    if (adviceScheduleImgRect.top <= 80) {
      adviceScheduleImg.classList.add('fixed');
    } else {
      if (adviceScheduleImg.classList.contains('fixed')) {
        adviceScheduleImg.classList.remove('fixed');
      }
    }
  };

  const topBannerAnimation = (container: sectionType['elements']['container']) => {
    const currentScrollPer = (yOffset / (container.current.clientHeight - window.innerHeight)) * 100;
    // 현재 스크롤 위치가 해당 scene의 몇 퍼센트 위치인지에 따라서 width height계산을 해준다
    // 현재 스크롤 위치를 1씬의 몇프로 위치인지 확인

    const bannerContent: any = container.current.querySelector('.for-lawyer__top-banner-content');
    if (bannerContent) {
      const calculatedWidth = window.innerWidth - currentScrollPer * (sections[currentScene].widthResizeNum || 1);
      bannerContent.style.width = `${
        calculatedWidth <= mobileImageWidthSizeInOneDay ? mobileImageWidthSizeInOneDay : calculatedWidth
      }px`;

      const calculatedHeight = window.innerHeight - currentScrollPer * (sections[currentScene].heightResizeNum || 1);
      bannerContent.style.height = `${
        calculatedHeight <= mobileImageHeightSizeInOneDay ? mobileImageHeightSizeInOneDay : calculatedHeight
      }px`;

      const BORDER_RADIUS = 19;
      bannerContent.style.borderRadius = `${BORDER_RADIUS * (currentScrollPer / 100)}px`;
    }

    if (!bannerContent && currentScrollPer < 132) {
      const mainBanner = sections[1].elements?.mobileImage?.current.querySelector('.for-lawyer__top-banner-content');
      mainBanner.classList.remove('invisible-background');
      const vlogTitle = mainBanner.querySelector('.for-lawyer-top-banner-vlog__title');
      vlogTitle.classList.remove('invisible');

      const firstMobileImage = mainBanner.querySelector('.for-lawyer-mobile-profile-image');
      const secondMobileImage = mainBanner.querySelector('.for-lawyer-mobile-profile-price-image');
      const thirdMobileImage = mainBanner.querySelector('.for-lawyer-mobile-profile-price-law-image');
      firstMobileImage.classList.remove('active');
      secondMobileImage.classList.remove('active');
      thirdMobileImage.classList.remove('active');

      container.current.appendChild(mainBanner);
    } else {
      if (!topBannerElem.current.classList.contains('sticky')) {
        topBannerElem.current.classList.add('sticky');
      }
    }
  };

  const oneDayAnimation = () => {
    oneDayContentElem.current.classList.add('active');
    const onedayScrollPer =
      ((yOffset - sections[0].elements?.container?.current.clientHeight) / oneDayElem.current.clientHeight) * 100;
    const mobileImageElem = document.querySelector('.for-lawyer__top-banner-content');
    if (!mobileImageElem) {
      return;
    }
    const firstMobileImage = mobileImageElem.querySelector('.for-lawyer-mobile-profile-image');
    const secondMobileImage = mobileImageElem.querySelector('.for-lawyer-mobile-profile-price-image');
    const thirdMobileImage = mobileImageElem.querySelector('.for-lawyer-mobile-profile-price-law-image');
    const sliceCircles = oneDayContentElem.current.querySelectorAll('.for-lawyer__slide-circle');

    if (onedayScrollPer >= 40) {
      //세번째 모바일 화면으로 변경
      firstMobileImage?.classList.remove('active');
      secondMobileImage?.classList.remove('active');
      thirdMobileImage?.classList.add('active');

      setActiveCircle(sliceCircles, 2);
    } else if (40 > onedayScrollPer && onedayScrollPer >= 20) {
      //두번째 모바일 화면으로 변경
      firstMobileImage?.classList.remove('active');
      secondMobileImage?.classList.add('active');
      thirdMobileImage?.classList.remove('active');

      setActiveCircle(sliceCircles, 1);
    } else {
      firstMobileImage?.classList.add('active');
      secondMobileImage?.classList.remove('active');
      thirdMobileImage?.classList.remove('active');

      setActiveCircle(sliceCircles, 0);
    }

    const containerRect = oneDayElem.current.getBoundingClientRect();
    if (containerRect.bottom <= window.innerHeight) {
      oneDayContentElem.current.classList.add('bottom-active');
    } else {
      oneDayContentElem.current.classList.remove('bottom-active');
    }
  };

  const adviceAnimation = (adviceContainer: sectionType['elements']['container']['current']) => {
    const adviceSectionScrollPer = ((yOffset - prevScrollHeight) / adviceContainer.clientHeight) * 100;

    const secondMImage = adviceContainer.querySelector('.for-lawyer-advice-step-2-img');
    const thirdMImage = adviceContainer.querySelector('.for-lawyer-advice-step-3-img');

    const firstStepDesc = adviceContainer.querySelector('.for-lawyer-advice-step-1');
    const secondStepDesc = adviceContainer.querySelector('.for-lawyer-advice-step-2');
    const thirdStepDesc = adviceContainer.querySelector('.for-lawyer-advice-step-3');

    const adviceSliceCircles = adviceContainer.querySelectorAll('.for-lawyer__slide-circle');

    if (adviceSectionScrollPer >= 60) {
      secondMImage.classList.remove('active');
      thirdMImage.classList.add('active');

      firstStepDesc.classList.remove('active');
      secondStepDesc.classList.remove('active');
      thirdStepDesc.classList.add('active');
      setActiveCircle(adviceSliceCircles, 2);
    } else if (adviceSectionScrollPer >= 33.3) {
      secondMImage.classList.add('active');
      thirdMImage.classList.remove('active');

      firstStepDesc.classList.remove('active');
      secondStepDesc.classList.add('active');
      thirdStepDesc.classList.remove('active');
      setActiveCircle(adviceSliceCircles, 1);
    } else {
      secondMImage.classList.remove('active');
      thirdMImage.classList.remove('active');

      firstStepDesc.classList.add('active');
      secondStepDesc.classList.remove('active');
      thirdStepDesc.classList.remove('active');
      setActiveCircle(adviceSliceCircles, 0);
    }

    const adviceContent = adviceContainer.querySelector('.for-lawyer__advice-content');
    adviceContent.classList.add('fixed');

    const adviceContainerRect = adviceContainer.getBoundingClientRect();
    if (adviceContainerRect.bottom <= window.innerHeight) {
      adviceContent.classList.add('fixed-none');
    } else {
      adviceContent.classList.remove('fixed-none');
    }
  };

  const adDayAnimation = (adDayContainer: sectionType['elements']['container']['current']) => {
    const adDaySectionScrollPer = ((yOffset - prevScrollHeight) / adDayContainer.clientHeight) * 100;

    const adDaySliceCircles = adDayContainer.querySelectorAll('.for-lawyer__slide-circle');
    const categoryAd = adDayContainer.querySelector('.for-lawyer-ad-type-img-1');
    const locationAd = adDayContainer.querySelector('.for-lawyer-ad-type-img-2');
    if (!isMobile) {
      categoryAd.classList.add('active');
      locationAd.classList.add('active');
      return;
    }

    if (adDaySectionScrollPer <= 50) {
      if (!categoryAd.classList.contains('active')) {
        categoryAd.classList.add('active');
      }
      locationAd.classList.remove('active');
      setActiveCircle(adDaySliceCircles, 0);
    } else {
      categoryAd.classList.remove('active');
      locationAd.classList.add('active');
      setActiveCircle(adDaySliceCircles, 1);
    }

    const fixElement = adDayContainer.querySelector('.for-lawyer__thirty-day-content-container');
    fixElement.classList.add('fixed');

    const adDayContainerRect = adDayContainer.getBoundingClientRect();
    if (adDayContainerRect.bottom <= window.innerHeight) {
      fixElement.classList.add('fixed-none');
    } else {
      fixElement.classList.remove('fixed-none');
    }
  };

  const adviceAfterAnimation = (adviceAfterContainer: sectionType['elements']['container']['current']) => {
    const adviceImage = adviceAfterContainer.querySelector('.for-lawyer__advice-after-image');
    const adviceImageRect = adviceImage.getBoundingClientRect();
    const fixImage = document.querySelector('.for-lawyer-manager-schedule-img');
    if (adviceImageRect.top <= 100) {
      fixImage?.classList.remove('fixed');
    }

    if (!isMobile) {
      return;
    }

    const adviceAfterScrollPer = ((yOffset - prevScrollHeight) / adviceAfterContainer.clientHeight) * 100;

    const adviceAfterSliceCircles = adviceAfterContainer.querySelectorAll('.for-lawyer__slide-circle');
    const managerAdviceListImg = adviceAfterContainer.querySelector('.for-lawyer-advice-list-img');
    const managerAdvicePriceImg = adviceAfterContainer.querySelector('.for-lawyer-advice-price-img');
    const managerAdviceReviewImg = adviceAfterContainer.querySelector('.for-lawyer-advice-review-img');
    if (adviceAfterScrollPer >= 60) {
      managerAdviceListImg.classList.remove('active');
      managerAdvicePriceImg.classList.remove('active');
      managerAdviceReviewImg.classList.add('active');

      setActiveCircle(adviceAfterSliceCircles, 2);
    } else if (adviceAfterScrollPer >= 33.3) {
      managerAdviceListImg.classList.remove('active');
      managerAdvicePriceImg.classList.add('active');
      managerAdviceReviewImg.classList.remove('active');

      setActiveCircle(adviceAfterSliceCircles, 1);
    } else {
      managerAdviceListImg.classList.add('active');
      managerAdvicePriceImg.classList.remove('active');
      managerAdviceReviewImg.classList.remove('active');

      setActiveCircle(adviceAfterSliceCircles, 0);
    }

    const contentContainer = adviceAfterContainer.querySelector('.for-lawyer__advice-after-content-container');
    contentContainer.classList.add('fixed');
    if (fixImage?.classList.contains('fixed')) {
      fixImage.classList.remove('fixed');
    }

    const adviceAfterRect = adviceAfterContainer.getBoundingClientRect();
    if (adviceAfterRect.bottom <= window.innerHeight) {
      contentContainer.classList.add('fixed-none');
    } else {
      contentContainer.classList.remove('fixed-none');
    }
  };

  const setAnimation = () => {
    const elements = sections[currentScene]?.elements;
    if (!elements) {
      return;
    }

    if (currentScene === 0 || currentScene === 1) {
      const firstSceneElem = sections[0]?.elements;
      resetTopBanner(firstSceneElem);
    }

    if (currentScene !== 1) {
      removeStickyOfOneDaySection();
    }

    if (currentScene !== 0 && currentScene !== 1) {
      //섹션2의 sticky해제
      if (topBannerElem.current.classList.contains('sticky')) {
        topBannerElem.current.classList.remove('sticky');
      }
    }

    if (currentScene !== 5) {
      removeStickyOfAdviceProcess();
    }

    if (isMobile && currentScene !== 6) {
      const fixElement = document.querySelector('.for-lawyer__thirty-day-content-container');
      if (fixElement) {
        fixElement.classList.remove('fixed');
      }
    }

    if (currentScene !== 10) {
      const fixElement = document.querySelector('.for-lawyer__advice-after-content-container');
      if (fixElement) {
        fixElement.classList.remove('fixed');
      }
    }
    if (currentScene === 8 || currentScene === 9) {
      const scheduleContainerElem = sections[9]?.elements;
      controlScheduleImage(scheduleContainerElem);
    }

    if (currentScene !== 9 && currentScene !== 10) {
      const fixElement = document.querySelector('.for-lawyer-manager-schedule-img');
      if (fixElement) {
        fixElement.classList.remove('fixed');
      }
    }

    switch (currentScene) {
      case 0:
        const container = elements?.container;
        topBannerAnimation(container);
        break;
      case 1:
        oneDayAnimation();
        break;
      case 5:
        const adviceContainer = elements?.container?.current;
        if (!adviceContainer) {
          return;
        }
        adviceAnimation(adviceContainer);

        break;
      case 6:
        const adDayContainer = elements?.container?.current;
        if (!adDayContainer) {
          return;
        }

        adDayAnimation(adDayContainer);

        break;
      case 10:
        const adviceAfterContainer = elements?.container?.current;
        if (!adviceAfterContainer) {
          return;
        }
        adviceAfterAnimation(adviceAfterContainer);
    }
  };

  const [scrollPercentage, setScrollPercentage] = useState(0);
  const setScrollPercentageCircle = () => {
    const bodyElement = document.querySelector('#preview__container');
    const totalHeight = bodyElement?.clientHeight || 0;

    const calcPercentage = (yOffset / (totalHeight - 1000)) * 100;
    setScrollPercentage(calcPercentage);
  };

  let prevScrollHeight = 0; // 현재 스크롤 위치(yOffset)보다 이전에 위치한 스크롤 섹션들의 스크롤 높이값의 합
  let currentScene = 0;
  const scrollLoop = () => {
    prevScrollHeight = 0;
    for (let i = 0; i < currentScene; i++) {
      prevScrollHeight += sections[i].scrollHeight;
    }

    if (yOffset > prevScrollHeight + sections[currentScene]?.scrollHeight) {
      currentScene++;
    }
    if (yOffset < prevScrollHeight) {
      if (currentScene === 0) return;
      currentScene--;
    }

    setAnimation();
    setScrollPercentageCircle();
  };

  let yOffset = 0; // window.pageYOffset 대신 쓸 변수
  const scrollEventHandler = _.throttle(() => {
    yOffset = window.pageYOffset;
    scrollLoop();
  }, 30);

  const initFirstSection = () => {
    const widthRatio = topBannerElem.current.clientWidth / mobileImageWidthSizeInOneDay;
    const heightRatio = topBannerElem.current.clientHeight / mobileImageHeightSizeInOneDay;

    // 1퍼센트당 움직여야 줄어들어야 하는 길이 계산
    sections[0].widthResizeNum =
      (sections[0].elements?.container?.current.clientWidth - mobileImageWidthSizeInOneDay) / 100;
    sections[0].heightResizeNum =
      (sections[0].elements?.container?.current.clientHeight - mobileImageHeightSizeInOneDay) / 100;

    sections[0].scrollExpandNum = (widthRatio > heightRatio ? widthRatio : heightRatio) + 2;

    const bannerContent: any = document.querySelector('.for-lawyer__top-banner-content');
    if (bannerContent) {
      bannerContent.style = '';
    }
  };

  const setObserver = () => {
    const viewPortElements = document.querySelectorAll('.view-on-visible');
    const viewPortCustomElements = document.querySelectorAll('.view-custom-on-visible');
    const visibleInViewPortElements = [...Array.from(viewPortElements), ...Array.from(viewPortCustomElements)];
    const iObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > 0) {
          setTimeout(() => {
            entry.target.classList.add('visible');
          }, 500);
        } else {
          entry.target.classList.remove('visible');
        }
      });
    });

    const startObserver = () => {
      visibleInViewPortElements.forEach((el) => {
        iObserver.observe(el);
      });
    };

    const cancelObserver = () => {
      visibleInViewPortElements.forEach((el) => {
        iObserver.unobserve(el);
      });
    };
    return { startObserver, cancelObserver };
  };

  function resizeHandler() {
    if (!isMobile) {
      window.location.reload();
    }
  }

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);
    window.addEventListener('scroll', scrollEventHandler);
    scrollTo(0, 0);

    initFirstSection();
    setLayout();
    setObserver().startObserver();
    return () => {
      window.removeEventListener('resize', resizeHandler);
      window.removeEventListener('scroll', scrollEventHandler);
      setObserver().cancelObserver();
    };
  }, [isMobile]);
  return (
    <div id="preview__container">
      <HeadMetaTag
        title="변호사 개업 미리보기"
        description="개업할까 말까? 김 변호사의 개업 생활을 살펴보세요."
        image={`${getStaticImageUrl({ fileName: 'preview/메타이미지_변호사랜딩페이지.jpg' })}`}
      />
      <PreviewWrapper>
        <section className="for-lawyer__top-banner-container sticky" ref={topBannerElem}>
          <TopBanner />
        </section>
        <section className="for-lawyer__oneday" ref={oneDayElem}>
          <OneDay oneDayContentElem={oneDayContentElem} oneDayMobileImg={oneDayMobileImg} />
        </section>
        <section className="for-lawyer__empty-lawyer-image" ref={emptyLawyerImageElem}>
          {isMobile ? (
            <img
              className="for-lawyer__empty-lawyer-image-card view-on-visible"
              src={getStaticImageUrl({ fileName: 'preview/lawyer-card.jpg' })}
            />
          ) : (
            <img
              className="for-lawyer__empty-lawyer-image-card view-on-visible"
              src={getStaticImageUrl({ fileName: 'preview/lawyer-card-desktop.png' })}
            />
          )}

          <div className="for-lawyer__empty-lawyer-image-desc-container">
            {isMobile ? (
              <img
                src={getStaticImageUrl({ fileName: 'preview/empty-desc-mobile.png' })}
                className="for-lawyer__empty-lawyer-image-desc mobile view-on-visible"
              />
            ) : (
              <img
                src={getStaticImageUrl({ fileName: 'preview/empty-desc-desktop.png' })}
                className="for-lawyer__empty-lawyer-image-desc desktop view-on-visible"
              />
            )}
          </div>
        </section>
        <div className="for-lawyer__advice-back">
          <section className="for-lawyer__lawyer-image" ref={lawyerImageElem}>
            <LawyerProfileImage isMobile={isMobile} />
          </section>

          <section className="for-lawyer__fourteen-day" ref={fourteenDayElem}>
            <FourteenDay isMobile={isMobile} />
          </section>
        </div>
        <section className="for-lawyer__advice" ref={adviceElem}>
          <AdviceProcess isMobile={isMobile} />
        </section>

        <section className="for-lawyer__thirty-day" ref={thirtyDayElem}>
          <ThirtyDay isMobile={isMobile} />
        </section>
        <section className="for-lawyer__ad-desc" ref={adDescElem}>
          <AdDesc isMobile={isMobile} />
        </section>
        <section className="for-lawyer__thirty-five-day" ref={thirtyFiveDayElem}>
          <ThirtyFiveDay isMobile={isMobile} />
        </section>
        <section className="for-lawyer__advice-schedule" ref={adviceScheduleElem}>
          <AdviceSchedule isMobile={isMobile} />
        </section>
        <section className="for-lawyer__advice-after" ref={adviceAfterElem}>
          <AdviceAfter isMobile={isMobile} />
        </section>
        <Guide isMobile={isMobile} />
        <footer className="for-lawyer__footer">
          <div className="for-lawyer__footer-content">
            <a href="https://www.lawtalk.co.kr" target="_blank">
              <LawtalkLogo />
            </a>
            <p className="for-lawyer__footer-copyright">Copyright© Law&Company. All rights reserved.</p>
          </div>
        </footer>
        <div className={cx('for-lawyer-fix-arrow', { 'move-arrow': scrollPercentage === 0 })}>
          {scrollPercentage < 100 && (
            <>
              <div className="for-lawyer-fix-arrow-circle">
                <Pie percentage={scrollPercentage} color="#FF5500" />
              </div>
              <div
                className={cx('for-lawyer-fix-arrow-icon', {
                  'active-scroll': scrollPercentage > 0,
                })}
              ></div>
            </>
          )}
        </div>
      </PreviewWrapper>
    </div>
  );
};

export default Preview;
